import React, { useState } from 'react';
import { Avatar, Badge, Box, IconButton } from '@mui/material';
import { useSelector } from 'react-redux';
import {
    updateUserData,
    userOriginalProfilePhotoSelector,
    userSelector,
} from 'redux/reducers/user';
import { CloudUpload, DeleteForever } from '@mui/icons-material';
import { getSignedUrl, uploadFile } from 'api';
import { useAppDispatch } from 'redux/store';
import { pushMessage } from 'redux/reducers/messages';
import { ButtonWithLoader } from 'components/Buttons';
import useTheme from '@mui/material/styles/useTheme';
import CropImageModal from 'components/Dialogs/CropImageModal';
import { useToggle } from 'utils';

const ProfileDetailsForm = () => {
    const theme = useTheme();
    const { userData } = useSelector(userSelector);
    const currentProfilePhoto = useSelector(userOriginalProfilePhotoSelector);
    const [profilePhoto, setProfilePhoto] = useState<File | null>();
    const [originalImageFile, setOriginalImageFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [uploadFailed, setUploadFailed] = useState<boolean>(false);
    const [cropModalVisible, toggleCropModal] = useToggle();
    const dispatch = useAppDispatch();

    const changeAvatarPhoto = (files: FileList | null) => {
        if (!files) {
            return;
        }

        Array.from(files).forEach((file) => {
            setOriginalImageFile(file);
            toggleCropModal(); // Open crop modal when file is selected
        });
    };

    const handleCropComplete = async (croppedFile: File) => {
        setProfilePhoto(croppedFile);
        setOriginalImageFile(null);
        toggleCropModal();
        await uploadProfilePhotoToS3(croppedFile);
    };

    const handleCropCancel = () => {
        setOriginalImageFile(null);
        toggleCropModal();
    };

    const undoProfileUpload = () => {
        setProfilePhoto(null);
    };

    const uploadProfilePhotoToS3 = async (fileToUpload?: File | null) => {
        const targetPhoto = fileToUpload || profilePhoto;
        if (targetPhoto) {
            setIsUploading(true);
            setUploadFailed(false);
            try {
                const response = await getSignedUrl();
                if (response.status === 201) {
                    const res = await uploadFile(response.data.url, targetPhoto);
                    if (res.status === 200) {
                        const newProfilePhoto = response.data.key;
                        const result = await dispatch(
                            updateUserData({
                                userUuid: userData?.uuid || '',
                                profile_photo: newProfilePhoto,
                                first_name: null,
                                last_name: null,
                                email: null,
                                user_name: null,
                                phone: null,
                            }),
                        );

                        if (updateUserData.fulfilled.match(result)) {
                            setProfilePhoto(null);
                            setUploadFailed(false);
                            dispatch(
                                pushMessage({
                                    status: 'success',
                                    message: 'Profile photo updated successfully.',
                                }),
                            );
                        } else {
                            setUploadFailed(true);
                            if (result.payload) {
                                // Server sent validation errors
                                if ('errors' in result.payload) {
                                    dispatch(
                                        pushMessage({
                                            status: 'error',
                                            message: `${result.payload?.errors}`,
                                        }),
                                    );
                                } else {
                                    // No validation errors
                                    // Something might be wrong on server side
                                    dispatch(
                                        pushMessage({
                                            status: 'error',
                                            message: 'Something went wrong, please try again',
                                        }),
                                    );
                                }
                            }
                        }
                    }
                }
                return false;
            } catch (err: any) {
                setUploadFailed(true);
                dispatch(
                    pushMessage({
                        status: 'error',
                        message: 'Something went wrong, please try again',
                    }),
                );
                return false;
            } finally {
                setIsUploading(false);
            }
        }
        return false;
    };

    return (
        <Box>
            <Box sx={{ marginTop: theme.spacing(6) }}>
                <Box my={2} textAlign="center">
                    <Badge
                        overlap="circular"
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        badgeContent={
                            isUploading ? (
                                ''
                            ) : (
                                <Box
                                    sx={{
                                        background: theme.palette.primary.light,
                                        borderRadius: '50%',
                                    }}
                                >
                                    {!profilePhoto && (
                                        <label htmlFor="icon-button-file">
                                            <input
                                                accept="image/*"
                                                style={{ display: 'none' }}
                                                id="icon-button-file"
                                                type="file"
                                                onChange={(event) => {
                                                    changeAvatarPhoto(event.target.files);
                                                }}
                                                name="profile_photo"
                                            />
                                            <IconButton
                                                color="primary"
                                                aria-label="upload picture"
                                                component="span"
                                                sx={{ color: 'white' }}
                                                size="large"
                                            >
                                                <CloudUpload />
                                            </IconButton>
                                        </label>
                                    )}
                                    {profilePhoto && (
                                        <IconButton
                                            color="primary"
                                            aria-label="upload picture"
                                            component="span"
                                            sx={{ color: 'white' }}
                                            onClick={undoProfileUpload}
                                            size="large"
                                        >
                                            <DeleteForever />
                                        </IconButton>
                                    )}
                                </Box>
                            )
                        }
                    >
                        <Avatar
                            alt="Travis Howard"
                            variant="rounded"
                            src={
                                profilePhoto
                                    ? URL.createObjectURL(profilePhoto)
                                    : currentProfilePhoto || ''
                            }
                            sx={{
                                width: theme.spacing(116),
                                height: theme.spacing(116),
                                variant: 'rounded',
                            }}
                        >
                            {userData?.first_name} {userData?.last_name}
                        </Avatar>
                    </Badge>
                </Box>
                <Box my={2} textAlign="center">
                    {uploadFailed && profilePhoto && (
                        <ButtonWithLoader
                            size="large"
                            type="submit"
                            color="primary"
                            variant="contained"
                            onClick={() => uploadProfilePhotoToS3()}
                            sx={{ marginTop: theme.spacing(6) }}
                            disabled={isUploading}
                            loading={isUploading}
                        >
                            {isUploading ? 'Retrying' : 'Retry Upload'}
                        </ButtonWithLoader>
                    )}
                </Box>
            </Box>
            {originalImageFile && (
                <CropImageModal
                    isOpen={cropModalVisible}
                    close={handleCropCancel}
                    uploadImage={URL.createObjectURL(originalImageFile)}
                    buttonText="Save"
                    onComplete={handleCropComplete}
                />
            )}
        </Box>
    );
};

export default ProfileDetailsForm;
